// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Full-resolution canvas for the visible region of a zoomed PDF page (enlarge-preview-annotate task 7.3; Req 2.11;
// design "page rendering"). At high zoom `PageImage` lowers the base canvas's pixel ratio so its area stays within
// MAX_CANVAS_PIXELS. This component then renders only the part of the page inside the scroll container's viewport at
// the screen's pixel ratio, and places it over the base canvas at the same spot, as pdf.js's own viewer does.
//
// Usage (Enlarged_Viewer): render it inside the page wrapper, a `position: relative` element of exactly the displayed
// page size that also holds `PageImage`, after `PageImage` and before the AnnotationLayer:
//
//   <div style={{ position: 'relative', width, height }}>
//     <PageImage ... />
//     <DetailCanvas pdf={loaded.pdf} page={page} width={size.width} height={size.height} scrollContainer={contentRef} />
//     <AnnotationLayer ... />
//   </div>
//
// It renders nothing when the base canvas already has the full pixel ratio.
import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

import type { PdfProxy } from './loadedDocument';
import { cappedPixelRatio } from './PageImage';

export interface DetailCanvasProps {
  pdf: PdfProxy;
  /** 1-based page. */
  page: number;
  /** Displayed page size in CSS px (the size `PageImage` reported through `onRendered`). */
  width: number;
  height: number;
  /** The scrolling content area that holds the page. Its scroll and size decide the visible region. */
  scrollContainer: RefObject<HTMLElement | null>;
  /** Screen pixel ratio; defaults to `window.devicePixelRatio`. */
  devicePixelRatio?: number;
}

/** Visible part of the page in page CSS px, or null when none of it is visible. */
export interface Region {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * The part of a `page` rectangle inside the `view` rectangle (both in the same coordinate space, e.g. client
 * coordinates), expressed relative to the page's top-left corner and rounded outward to whole CSS px.
 */
export function visibleRegion(
  page: { left: number; top: number; width: number; height: number },
  view: { left: number; top: number; width: number; height: number },
): Region | null {
  const x0 = Math.max(0, Math.floor(view.left - page.left));
  const y0 = Math.max(0, Math.floor(view.top - page.top));
  const x1 = Math.min(page.width, Math.ceil(view.left + view.width - page.left));
  const y1 = Math.min(page.height, Math.ceil(view.top + view.height - page.top));
  if (!(x1 > x0 && y1 > y0)) return null;
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** True when `PageImage` lowered the base canvas's pixel ratio for a `width × height` page. */
export function needsDetail(width: number, height: number, dpr: number): boolean {
  return cappedPixelRatio(width, height, dpr) < dpr;
}

function isCancelled(e: unknown): boolean {
  return typeof e === 'object' && e !== null && (e as { name?: unknown }).name === 'RenderingCancelledException';
}

type RenderTask = { promise: Promise<unknown>; cancel: () => void };

export function DetailCanvas(props: DetailCanvasProps) {
  const dpr = props.devicePixelRatio ?? (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
  if (!(props.width > 0 && props.height > 0) || !needsDetail(props.width, props.height, dpr)) return null;
  return <DetailRenderer {...props} dpr={dpr} />;
}

function DetailRenderer({ pdf, page, width, height, scrollContainer, dpr }: DetailCanvasProps & { dpr: number }) {
  const hostRef = useRef<HTMLDivElement>(null);

  // A page, size, or ratio change makes the shown detail stale: drop it before the browser paints.
  useLayoutEffect(() => {
    hostRef.current?.replaceChildren();
  }, [pdf, page, width, height, dpr]);

  useEffect(() => {
    const host = hostRef.current;
    const container = scrollContainer.current;
    if (!host || !container) return;
    let disposed = false;
    let frame: number | null = null;
    let task: RenderTask | null = null;
    let pageProxy: Awaited<ReturnType<PdfProxy['getPage']>> | null = null;

    const draw = async () => {
      frame = null;
      const wrapper = host.parentElement;
      if (!wrapper) return;
      const pr = wrapper.getBoundingClientRect();
      const cr = container.getBoundingClientRect();
      const region = visibleRegion(
        { left: pr.left, top: pr.top, width, height },
        {
          left: cr.left + container.clientLeft,
          top: cr.top + container.clientTop,
          width: container.clientWidth,
          height: container.clientHeight,
        },
      );
      task?.cancel();
      task = null;
      if (!region) return;
      try {
        pageProxy ??= await pdf.getPage(page);
        if (disposed) return;
        // The visible region can itself be large (a big screen at a high ratio); keep it within the canvas limit.
        const ratio = cappedPixelRatio(region.w, region.h, dpr);
        const base = pageProxy.getViewport({ scale: 1 });
        const viewport = pageProxy.getViewport({ scale: (width / base.width) * ratio });
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(region.w * ratio);
        canvas.height = Math.ceil(region.h * ratio);
        Object.assign(canvas.style, {
          position: 'absolute',
          left: `${region.x}px`,
          top: `${region.y}px`,
          width: `${region.w}px`,
          height: `${region.h}px`,
        });
        const current = pageProxy.render({
          canvas,
          viewport,
          transform: [1, 0, 0, 1, -region.x * ratio, -region.y * ratio],
        });
        task = current;
        await current.promise;
        if (disposed || task !== current) return;
        task = null;
        // Swap in the finished canvas, so a partly drawn region is never shown.
        host.replaceChildren(canvas);
      } catch (e) {
        // The base canvas stays visible; the next scroll or resize retries. Log the kind only, never page content.
        if (!disposed && !isCancelled(e))
          console.error('Detail render failed:', e instanceof Error ? e.name : 'unknown');
      }
    };

    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(() => void draw());
    };

    schedule();
    container.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    observer?.observe(container);
    return () => {
      disposed = true;
      if (frame !== null) cancelAnimationFrame(frame);
      task?.cancel();
      container.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
    };
  }, [pdf, page, width, height, dpr, scrollContainer]);

  return (
    <div
      ref={hostRef}
      aria-hidden="true"
      data-testid="detail-canvas"
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}
    />
  );
}
